/**
 * Calendar Store - Manages calendar availability and pricing data
 *
 * This store handles:
 * - Fetching availability and pricing data from separate API endpoints
 * - Caching responses to prevent duplicate requests during the page session
 * - Progressive loading (show availability immediately, update with pricing when ready)
 * - Per-pitchtype data storage (each pitchtype maintains its own cached data)
 *
 * Data flow:
 * 1. Raw API responses (availability/pricing) are fetched and cached
 * 2. Data is transformed into datepicker format for UI consumption
 * 3. Components access transformed data via reactive getters
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AvailabilityCalendarRepositoryFetch } from '~/apps/calendar-unified/AvailabilityCalendarRepositoryFetch'
import { PricingCalendarRepositoryFetch } from '~/apps/calendar-unified/PricingCalendarRepositoryFetch'
import {
  transformToDatepickerAvailability,
  mergeAvailabilityAndPricing,
} from '~/apps/calendar-unified/dataTransformer'
import {
  reportApiError,
  reportAppError,
} from '~/utility/sentry/apiErrorReporter'
import type {
  AvailabilityCalendarResponse,
  PricingCalendarResponse,
  UnifiedCalendarParams,
  ComponentCalendarParams,
} from '~/apps/calendar-unified/schemas'

interface CalendarCacheEntry {
  data: AvailabilityCalendarResponse | PricingCalendarResponse
  // Don't cache transformed data - transform on-the-fly to avoid duplication
}

interface CalendarStoreState {
  cache: Record<string, CalendarCacheEntry>
  loading: Map<string, Promise<any>> // Keep as Map - promises can't be serialized
  errors: Record<string, Error>
}

export const useCalendarStore = defineStore('calendar', () => {
  const state = ref<CalendarStoreState>({
    cache: {},
    loading: new Map(), // Keep as Map - promises can't be serialized
    errors: {},
  })

  const availabilityRepo = new AvailabilityCalendarRepositoryFetch()
  const pricingRepo = new PricingCalendarRepositoryFetch()

  // Filter parameters for availability endpoint (synchronous)
  function filterAvailabilityParams(
    params: Record<string, any>,
  ): Record<string, any> {
    const apiParams: Record<string, any> = {}

    Object.entries(params).forEach(([key, value]) => {
      // Skip component-only parameters and nights (not needed for availability)
      if (
        key === 'slug' ||
        key === 'categories' ||
        key === 'arrive' ||
        key === 'depart' ||
        key === 'nights'
      ) {
        return
      }

      // Handle facets → facet remapping for API consistency
      if (key === 'facets') {
        apiParams.facet = Array.isArray(value) ? [...value].sort() : [value]
        return
      }

      // Only include non-empty values
      if (value !== undefined && value !== null && value !== '') {
        apiParams[key] = value
      }
    })

    return apiParams
  }

  // Filter parameters for pricing endpoint (synchronous)
  function filterPricingParams(
    params: Record<string, any>,
  ): Record<string, any> {
    const apiParams: Record<string, any> = {}

    Object.entries(params).forEach(([key, value]) => {
      // Skip component-only parameters but keep nights
      if (
        key === 'slug' ||
        key === 'categories' ||
        key === 'arrive' ||
        key === 'depart'
      ) {
        return
      }

      // Handle facets → facet remapping for API consistency
      if (key === 'facets') {
        apiParams.facet = Array.isArray(value) ? [...value].sort() : [value]
        return
      }

      // Only include non-empty values
      if (value !== undefined && value !== null && value !== '') {
        apiParams[key] = value
      }
    })

    return apiParams
  }

  // Generate cache key from parameters
  function generateCacheKey(
    type: 'availability' | 'pricing',
    params: Record<string, any>,
  ): string {
    // Create a consistent key by sorting parameters
    const sortedParams = Object.keys(params)
      .sort()
      .reduce(
        (result, key) => {
          if (params[key] !== undefined && params[key] !== null) {
            result[key] = params[key]
          }
          return result
        },
        {} as Record<string, any>,
      )

    return `${type}:${JSON.stringify(sortedParams)}`
  }

  // Get availability data from cache or fetch
  async function getAvailability(
    langCode: string,
    params: Omit<ComponentCalendarParams, 'nights'>, // Component params without nights
    signal?: AbortSignal,
  ): Promise<AvailabilityCalendarResponse> {
    const filtered = filterAvailabilityParams(params)
    const cacheKey = generateCacheKey('availability', filtered)

    // Return cached data if exists
    const cached = state.value.cache[cacheKey]
    if (cached) {
      return cached.data as AvailabilityCalendarResponse
    }

    // Return existing promise if already loading
    const existingPromise = state.value.loading.get(cacheKey)
    if (existingPromise) {
      return existingPromise as Promise<AvailabilityCalendarResponse>
    }

    // Create new fetch promise
    const promise = (async () => {
      try {
        const apiParams = filtered
        const data = await availabilityRepo.get(langCode, apiParams, signal)

        // Cache only the raw data
        const cacheEntry: CalendarCacheEntry = {
          data,
        }
        state.value.cache[cacheKey] = cacheEntry

        // Clear loading state
        state.value.loading.delete(cacheKey)
        delete state.value.errors[cacheKey]

        return data
      } catch (error: any) {
        state.value.loading.delete(cacheKey)

        // Don't cache AbortErrors as they're just cancellations, not real errors
        if (error instanceof Error && error.name !== 'AbortError') {
          // Report error using structured Sentry approach
          if (
            error.name === 'CalendarFetchError' ||
            error.name === 'CalendarValidationError'
          ) {
            reportApiError(error, 'calendar availability fetch', {
              cacheKey,
              langCode,
            })
          } else {
            // Fallback for unexpected errors
            reportAppError(error, 'calendar availability fetch', {
              params: JSON.stringify(params),
              langCode,
              cacheKey,
            })
          }
          state.value.errors[cacheKey] = error as Error
        }
        throw error
      }
    })()

    // Store loading promise
    state.value.loading.set(cacheKey, promise)

    return promise
  }

  // Get pricing data from cache or fetch
  async function getPricing(
    langCode: string,
    params: ComponentCalendarParams &
      Required<Pick<ComponentCalendarParams, 'nights'>>, // Must include nights
    signal?: AbortSignal,
  ): Promise<PricingCalendarResponse> {
    const filtered = filterPricingParams(params)
    const cacheKey = generateCacheKey('pricing', filtered)

    // Return cached data if exists
    const cached = state.value.cache[cacheKey]
    if (cached) {
      return cached.data as PricingCalendarResponse
    }

    // Return existing promise if already loading
    const existingPromise = state.value.loading.get(cacheKey)
    if (existingPromise) {
      return existingPromise as Promise<PricingCalendarResponse>
    }

    // Create new fetch promise
    const promise = (async () => {
      try {
        const apiParams = filtered
        const data = await pricingRepo.get(langCode, apiParams, signal)

        // Cache the result
        const cacheEntry: CalendarCacheEntry = {
          data,
        }
        state.value.cache[cacheKey] = cacheEntry

        // Clear loading state
        state.value.loading.delete(cacheKey)
        delete state.value.errors[cacheKey]

        return data
      } catch (error: any) {
        state.value.loading.delete(cacheKey)

        // Don't cache AbortErrors as they're just cancellations, not real errors
        if (error instanceof Error && error.name !== 'AbortError') {
          // Report error using structured Sentry approach
          if (
            error.name === 'CalendarFetchError' ||
            error.name === 'CalendarValidationError'
          ) {
            reportApiError(error, 'calendar pricing fetch', {
              cacheKey,
              langCode,
            })
          } else {
            // Fallback for unexpected errors
            reportAppError(error, 'calendar pricing fetch', {
              params: JSON.stringify(params),
              langCode,
              cacheKey,
            })
          }
          state.value.errors[cacheKey] = error as Error
        }
        throw error
      }
    })()

    // Store loading promise
    state.value.loading.set(cacheKey, promise)

    return promise
  }

  // Get both availability and pricing data
  async function getCalendarData(
    langCode: string,
    params: ComponentCalendarParams,
    signal?: AbortSignal,
  ): Promise<void> {
    // Split the date range into individual months for better caching
    const availabilityParams = filterAvailabilityParams(params)
    const months = splitIntoMonths(
      availabilityParams.start,
      availabilityParams.end,
    )

    // Fetch each month in parallel (they'll be cached individually)
    const availabilityPromises = months.map((month) =>
      getAvailability(
        langCode,
        { ...availabilityParams, start: month.start, end: month.end },
        signal,
      ),
    )

    // Only fetch pricing if nights parameter is provided
    const pricingParams = filterPricingParams(params)
    const pricingPromises =
      params.nights && params.nights > 0
        ? months.map((month) =>
            getPricing(
              langCode,
              {
                ...pricingParams,
                start: month.start,
                end: month.end,
              } as UnifiedCalendarParams & { nights: number },
              signal,
            ),
          )
        : []

    // Wait for all data to be fetched and cached
    await Promise.all([
      Promise.allSettled(availabilityPromises),
      Promise.allSettled(pricingPromises),
    ])
  }

  // Check if data is loading
  function isLoading(
    type: 'availability' | 'pricing',
    params: ComponentCalendarParams,
  ): boolean {
    const filtered =
      type === 'availability'
        ? filterAvailabilityParams(params)
        : filterPricingParams(params)
    const cacheKey = generateCacheKey(type, filtered)
    return state.value.loading.has(cacheKey)
  }

  // Get error for specific params
  function getError(
    type: 'availability' | 'pricing',
    params: ComponentCalendarParams,
  ): Error | undefined {
    const filtered =
      type === 'availability'
        ? filterAvailabilityParams(params)
        : filterPricingParams(params)
    const cacheKey = generateCacheKey(type, filtered)
    return state.value.errors[cacheKey]
  }

  // Clear all cache
  function clearCache(): void {
    state.value.cache = {}
    state.value.loading.clear() // Keep Map.clear() for loading
    state.value.errors = {}
  }

  // Split a date range into individual months
  function splitIntoMonths(
    start?: string,
    end?: string,
  ): Array<{ start: string; end: string }> {
    if (!start || !end) return []

    const months: Array<{ start: string; end: string }> = []
    const startDate = new Date(start)
    const endDate = new Date(end)

    let currentDate = new Date(startDate)

    while (currentDate < endDate) {
      const monthStart = new Date(
        Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1),
      )
      const monthEnd = new Date(
        Date.UTC(
          currentDate.getUTCFullYear(),
          currentDate.getUTCMonth() + 1,
          0,
        ),
      )

      // Ensure we don't go past the requested end date
      const actualEnd = monthEnd > endDate ? endDate : monthEnd

      months.push({
        start: monthStart.toISOString().split('T')[0],
        end: actualEnd.toISOString().split('T')[0],
      })

      // Move to next month
      currentDate = new Date(
        Date.UTC(
          currentDate.getUTCFullYear(),
          currentDate.getUTCMonth() + 1,
          1,
        ),
      )
    }

    return months
  }

  // Merge availability results from multiple months
  function mergeMonthlyAvailability(
    results: PromiseSettledResult<any>[],
  ): AvailabilityCalendarResponse | null {
    const successfulResults = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
      .map((r) => r.value.data)

    if (successfulResults.length === 0) return null

    // Merge all calendar_data objects
    const mergedCalendarData: any = {}
    successfulResults.forEach((result) => {
      Object.assign(mergedCalendarData, result.calendar_data)
    })

    // Use metadata from first result, update window to cover full range
    const firstResult = successfulResults[0]
    const lastResult = successfulResults[successfulResults.length - 1]

    // Find the first and last results that actually have window data
    const firstWithWindow = successfulResults.find((result) => result.window)
    const lastWithWindow = [...successfulResults]
      .reverse()
      .find((result) => result.window)

    return {
      ...firstResult,
      calendar_data: mergedCalendarData,
      window:
        firstWithWindow && lastWithWindow
          ? {
              start: firstWithWindow.window.start,
              end: lastWithWindow.window.end,
            }
          : undefined,
    }
  }

  // Merge pricing results from multiple months
  function mergeMonthlyPricing(
    results: PromiseSettledResult<any>[],
  ): PricingCalendarResponse | null {
    const successfulResults = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
      .map((r) => r.value)

    if (successfulResults.length === 0) return null

    // Merge all pricing_data arrays
    const mergedPricingData: any[] = []
    successfulResults.forEach((result) => {
      mergedPricingData.push(...result.pricing_data)
    })

    // Use metadata from first result, update window to cover full range
    const firstResult = successfulResults[0]
    const lastResult = successfulResults[successfulResults.length - 1]

    // Find the first and last results that actually have window data
    const firstWithWindow = successfulResults.find((result) => result.window)
    const lastWithWindow = [...successfulResults]
      .reverse()
      .find((result) => result.window)

    return {
      ...firstResult,
      pricing_data: mergedPricingData,
      window:
        firstWithWindow && lastWithWindow
          ? {
              start: firstWithWindow.window.start,
              end: lastWithWindow.window.end,
            }
          : undefined,
    }
  }

  // Stats for debugging
  const stats = computed(() => ({
    cacheSize: Object.keys(state.value.cache).length,
    loadingCount: state.value.loading.size, // Keep .size for Map
    errorCount: Object.keys(state.value.errors).length,
    cacheEntries: Object.keys(state.value.cache),
  }))

  // Get cached availability months for a date range - components use this to access monthly data
  function getCachedMonthsForRange(
    langCode: string,
    params: ComponentCalendarParams,
  ): any[] {
    try {
      const availabilityParams = filterAvailabilityParams(params)
      const months = splitIntoMonths(
        availabilityParams.start,
        availabilityParams.end,
      )

      // Collect raw availability responses
      const availabilityResponses: AvailabilityCalendarResponse[] = []

      for (const month of months) {
        const monthParams = {
          ...availabilityParams,
          start: month.start,
          end: month.end,
        }
        const cacheKey = generateCacheKey('availability', monthParams)
        const cached = state.value.cache[cacheKey]

        if (cached?.data) {
          availabilityResponses.push(
            cached.data as AvailabilityCalendarResponse,
          )
        }
      }

      if (availabilityResponses.length === 0) {
        return []
      }

      // Merge availability data
      const mergedAvailability = mergeMonthlyAvailability(
        availabilityResponses.map((data) => ({
          status: 'fulfilled' as const,
          value: { data },
        })),
      )

      if (!mergedAvailability) {
        return []
      }

      // Check if we need to merge pricing data
      const needsPricing = params.nights && params.nights > 0
      let finalData = mergedAvailability

      if (needsPricing) {
        const pricingData = getCachedPricingForRange(
          langCode,
          params as ComponentCalendarParams &
            Required<Pick<ComponentCalendarParams, 'nights'>>,
        )

        if (pricingData) {
          // Merge availability and pricing on-the-fly
          finalData = mergeAvailabilityAndPricing(
            mergedAvailability,
            pricingData,
            params.nights,
          )
        }
      }

      // Transform to datepicker format on-the-fly
      return transformToDatepickerAvailability(finalData)
    } catch (error: any) {
      // Catch calendar merge errors to prevent fragmentation
      reportAppError(error, 'calendar data transformation', {
        langCode,
        paramKeys: Object.keys(params),
        errorLocation: 'getCachedMonthsForRange',
        errorName: error.name || 'UnknownError',
      })
      // Return empty array to prevent component crashes
      return []
    }
  }

  // Get cached pricing months for a date range (when dates are selected)
  function getCachedPricingForRange(
    langCode: string,
    params: ComponentCalendarParams &
      Required<Pick<ComponentCalendarParams, 'nights'>>,
  ): PricingCalendarResponse | null {
    const pricingParams = filterPricingParams(params)
    const months = splitIntoMonths(pricingParams.start, pricingParams.end)

    const monthlyPricingData: any[] = []
    for (const month of months) {
      const monthParams = {
        ...pricingParams,
        start: month.start,
        end: month.end,
      }
      const cacheKey = generateCacheKey('pricing', monthParams)
      const cached = state.value.cache[cacheKey]

      if (cached?.data) {
        const pricingResponse = cached.data as PricingCalendarResponse
        monthlyPricingData.push(...(pricingResponse.pricing_data || []))
      }
    }

    if (monthlyPricingData.length === 0) return null

    // Return merged pricing response
    return {
      pricing_data: monthlyPricingData,
      window: {
        start: pricingParams.start || '',
        end: pricingParams.end || '',
      },
    } as PricingCalendarResponse
  }

  // Check if we have all required cache entries for given params
  function hasCachedData(
    langCode: string,
    params: ComponentCalendarParams,
  ): boolean {
    const availabilityParams = filterAvailabilityParams(params)
    const months = splitIntoMonths(
      availabilityParams.start,
      availabilityParams.end,
    )

    // Check if all required months are cached
    for (const month of months) {
      const monthParams = {
        ...availabilityParams,
        start: month.start,
        end: month.end,
      }
      const cacheKey = generateCacheKey('availability', monthParams)
      if (!state.value.cache[cacheKey]) {
        return false
      }
    }

    // If pricing is needed, check pricing cache too
    if (params.nights && params.nights > 0) {
      const pricingParams = filterPricingParams(params)
      for (const month of months) {
        const monthParams = {
          ...pricingParams,
          start: month.start,
          end: month.end,
        }
        const cacheKey = generateCacheKey('pricing', monthParams)
        if (!state.value.cache[cacheKey]) {
          return false
        }
      }
    }

    return true
  }

  // High-level component interface - handles all the complexity
  async function loadComponentData(
    langCode: string,
    componentParams: {
      pitchtypeId: string
      campsiteSlug?: string
      stayDates?: { start?: string; end?: string }
      party: { adults: number; dogs: number; childAges?: number[] }
      facets?: string[]
      dateRange: { start: string; end: string }
    },
  ): Promise<void> {
    // Normalize parameters for store
    const storeParams: ComponentCalendarParams = {
      pitchtype_id: componentParams.pitchtypeId,
      adults: componentParams.party.adults,
      child_ages:
        componentParams.party.childAges?.sort((a, b) => a - b).join(',') || '',
      dogs: componentParams.party.dogs,
      facet: componentParams.facets?.sort() || [],
      start: componentParams.dateRange.start,
      end: componentParams.dateRange.end,
    }

    // Add nights if user has selected dates
    if (componentParams.stayDates?.start && componentParams.stayDates?.end) {
      const arriveDate = new Date(componentParams.stayDates.start)
      const departDate = new Date(componentParams.stayDates.end)
      const diffTime = departDate.getTime() - arriveDate.getTime()
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      storeParams.nights = nights
    }

    await getCalendarData(langCode, storeParams)
  }

  function getComponentData(
    langCode: string,
    componentParams: {
      pitchtypeId: string
      campsiteSlug?: string
      stayDates?: { start?: string; end?: string }
      party: { adults: number; dogs: number; childAges?: number[] }
      facets?: string[]
      dateRange: { start: string; end: string }
    },
  ): any[] {
    // Normalize parameters same way as loadComponentData
    const storeParams: ComponentCalendarParams = {
      pitchtype_id: componentParams.pitchtypeId,
      adults: componentParams.party.adults,
      child_ages:
        componentParams.party.childAges?.sort((a, b) => a - b).join(',') || '',
      dogs: componentParams.party.dogs,
      facet: componentParams.facets?.sort() || [],
      start: componentParams.dateRange.start,
      end: componentParams.dateRange.end,
    }

    if (componentParams.stayDates?.start && componentParams.stayDates?.end) {
      const arriveDate = new Date(componentParams.stayDates.start)
      const departDate = new Date(componentParams.stayDates.end)
      const diffTime = departDate.getTime() - arriveDate.getTime()
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      storeParams.nights = nights
    }

    return getCachedMonthsForRange(langCode, storeParams)
  }

  function hasComponentData(
    langCode: string,
    componentParams: {
      pitchtypeId: string
      campsiteSlug?: string
      stayDates?: { start?: string; end?: string }
      party: { adults: number; dogs: number; childAges?: number[] }
      facets?: string[]
      dateRange: { start: string; end: string }
    },
  ): boolean {
    // Normalize parameters same way as loadComponentData
    const storeParams: ComponentCalendarParams = {
      pitchtype_id: componentParams.pitchtypeId,
      adults: componentParams.party.adults,
      child_ages:
        componentParams.party.childAges?.sort((a, b) => a - b).join(',') || '',
      dogs: componentParams.party.dogs,
      facet: componentParams.facets?.sort() || [],
      start: componentParams.dateRange.start,
      end: componentParams.dateRange.end,
    }

    if (componentParams.stayDates?.start && componentParams.stayDates?.end) {
      const arriveDate = new Date(componentParams.stayDates.start)
      const departDate = new Date(componentParams.stayDates.end)
      const diffTime = departDate.getTime() - arriveDate.getTime()
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      storeParams.nights = nights
    }

    return hasCachedData(langCode, storeParams)
  }

  function generateComponentCacheKey(
    componentParams: {
      pitchtypeId: string
      campsiteSlug?: string
      stayDates?: { start?: string; end?: string }
      dateRange: { start: string; end: string }
    },
    lang: string,
  ): string {
    const baseKey = `calendar-${componentParams.campsiteSlug || 'unknown'}-${componentParams.pitchtypeId}-${lang}`

    // Include dates in key if user has selected them
    if (componentParams.stayDates?.start && componentParams.stayDates?.end) {
      return `${baseKey}-${componentParams.stayDates.start}-${componentParams.stayDates.end}`
    }

    // Include date range for undated calendar view
    return `${baseKey}-${componentParams.dateRange.start}-${componentParams.dateRange.end}`
  }

  return {
    // State - must be returned for SSR hydration
    state,

    // Low-level actions (for direct use if needed)
    getAvailability,
    getPricing,
    getCalendarData,
    isLoading,
    getError,
    clearCache,

    // Low-level cache accessors
    getCachedMonthsForRange,
    getCachedPricingForRange,
    hasCachedData,

    // High-level component interface (recommended)
    loadComponentData,
    getComponentData,
    hasComponentData,
    generateComponentCacheKey,

    // Getters
    stats,
  }
})
